// #genai reusable split layout: bg panel + right white panel + optional footer
import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@eagleview/ev-comp-library';
import { DEFAULT_BG_IMAGE_URL, DEFAULT_LOGO_URL } from '../../constants/AppConstants';

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    width: '100vw',
    position: 'fixed',
    top: 0,
    left: 0,
    fontFamily: 'Noto Sans, sans-serif',
    margin: 0,
    padding: 0,
    overflow: 'auto',
  },
  content: {
    flex: 1,
    display: 'flex',
    alignItems: 'stretch',
    position: 'relative',
  },
  bg: {
    position: 'static',
    flex: 1,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    '@media screen and (max-width: 600px)': {
      display: 'none',
    },
  },
  panelWrapper: {
    letterSpacing: '0.03em',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    width: 'var(--ev-sidebar-width, 509px)',
    background: '#FFF',
    padding: 0,
    '@media screen and (max-width: 600px)': {
      width: '100%',
    },
  },
  panel: {
    width: '100%',
    '@media screen and (max-width: 600px)': {
      padding: '24px',
    },
  },
  logo: {
    marginBottom: '32px',
    paddingBottom: '24px',
    borderBottom: '1px solid #C8CDD7',
    width: '100%',
    padding: '24px 48px',
    display: 'flex',
    justifyContent: 'center',
    '& img': {
      width: '360px',
      height: '46px',
      objectFit: 'contain',
      '@media screen and (max-width: 600px)': {
        width: '240px',
        height: '30px',
      },
    },
  },
  message: {
    padding: '0px 48px',
    '& h1': {
      color: '#1B202C',
      fontFamily: 'Noto Sans, sans-serif',
      fontSize: '32px',
      fontStyle: 'normal',
      fontWeight: 300,
      lineHeight: '48px',
      letterSpacing: '0%',
      textTransform: 'capitalize',
      textAlign: 'left',
      margin: 0,
    },
    '& p': {
      color: '#1B202C',
      fontFamily: 'Noto Sans, sans-serif',
      fontSize: '16px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '24px',
      letterSpacing: '0%',
      margin: 0,
    },
  },
  contentMobile: {
    '@media screen and (max-width: 600px)': {
      background: '#FFF',
    },
  },
}));

// #genai
// Accepts either:
//   - title + paragraph props for simple /verify-style pages
//   - children for multi-state or complex panel content
const AuthSplitLayout = ({
  children,
  title,
  paragraph,
  sidebarWidth,
  bgImageUrl,
  logoUrl,
  footer,
}) => {
  const classes = useStyles();
  return (
    <div
      className={classes.container}
      style={{
        '--ev-sidebar-width': sidebarWidth || undefined,
      }}
    >
      <main className={`${classes.content} ${classes.contentMobile}`}>
        <div className={classes.bg} style={{ backgroundImage: `url(${bgImageUrl})` }} />
        <div className={classes.panelWrapper}>
          <div className={classes.panel}>
            <div className={classes.logo}>
              <img src={logoUrl} alt="EagleView" />
            </div>
            <div className={classes.message}>
              {children || (
                <>
                  {title && <h1>{title}</h1>}
                  {paragraph}
                </>
              )}
            </div>
          </div>
        </div>
      </main>

      {footer}
    </div>
  );
};

AuthSplitLayout.propTypes = {
  children: PropTypes.node,
  title: PropTypes.string,
  paragraph: PropTypes.node,
  sidebarWidth: PropTypes.string,
  bgImageUrl: PropTypes.string,
  logoUrl: PropTypes.string,
  footer: PropTypes.node,
};

AuthSplitLayout.defaultProps = {
  children: null,
  title: null,
  paragraph: null,
  sidebarWidth: '509px',
  bgImageUrl: DEFAULT_BG_IMAGE_URL,
  logoUrl: DEFAULT_LOGO_URL,
  footer: null,
};

export default AuthSplitLayout;
